import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Alert,
  type SelectChangeEvent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { FilterChips } from "../components/FilterChips";
import { UsageTable } from "../components/UsageTable";
import {
  CUSTOMERS,
  GROUP_BY_OPTIONS,
  type FilterEntry,
  type GroupBy,
  type UsageResponse,
} from "../types";
import { fetchUsage, BillingApiError } from "../api/billingApi";

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
// Treat the picked end date as inclusive through end-of-day, so events emitted
// later the same day (after page load) are still within range on refresh.
const endOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

export function UsagePage() {
  const [customerId, setCustomerId] = useState<string>(CUSTOMERS[0]?.value ?? "stage");
  const [groupBy, setGroupBy] = useState<GroupBy | "">("case_id");
  const [filters, setFilters] = useState<FilterEntry[]>([]);
  const [start, setStart] = useState<Date | null>(startOfMonth(new Date()));
  const [end, setEnd] = useState<Date | null>(new Date());

  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!start || !end) {
      setError("Pick a start and end date");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchUsage({
        customerId,
        start,
        end: endOfDay(end),
        groupBy: groupBy || undefined,
        filters: Object.fromEntries(filters.map((f) => [f.column, f.value])),
      });
      setData(result);
    } catch (err) {
      if (err instanceof BillingApiError) {
        setError(`${err.status}: ${err.detail}`);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3}>
        <Typography variant="h5">Usage</Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer</InputLabel>
                <Select
                  label="Customer"
                  value={customerId}
                  onChange={(e: SelectChangeEvent) => setCustomerId(e.target.value)}
                >
                  {CUSTOMERS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Group by</InputLabel>
                <Select
                  label="Group by"
                  value={groupBy}
                  onChange={(e: SelectChangeEvent) => setGroupBy(e.target.value as GroupBy | "")}
                >
                  <MenuItem value="">
                    <em>None (total)</em>
                  </MenuItem>
                  {GROUP_BY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <DatePicker
                label="From"
                value={start}
                onChange={setStart}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <DatePicker
                label="To"
                value={end}
                onChange={setEnd}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={refresh}
                disabled={loading || !start || !end}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <FilterChips filters={filters} onChange={setFilters} />
          </Box>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <UsageTable data={data} loading={loading} />
      </Stack>
    </LocalizationProvider>
  );
}
